import { useEffect, useRef, useState } from "preact/hooks";
import { api, type Attachment, type Delivery, type Fetching, type Message, type SearchParams, type ThreadRow } from "./api";
import { fitFixedWidths, frameDocument, hasRemoteContent } from "./frame";
import { attachmentKind, fullDate, listDate, sender, showDelivery, size } from "./format";
import { Icon } from "./icons";
import { NetworkError, isNetworkError, isOnline, useOnline } from "./net";
import { cachedMessage, loadAttachment } from "./offline";
import { type SingleAction, actionFolder } from "./outbox";
import { hasMoreRecipients } from "./outgoing";
import type { Change } from "./overrides";
import { PdfViewer } from "./PdfViewer";
import type { ComposeMode } from "./route";

interface Props {
  id: string;
  folders: string[];
  /** The folder the message was opened from (null: a search). */
  folder: string | null;
  /** The Yahoo address (null until known): decides whether "reply all" reaches anyone more than "reply". */
  me: string | null;
  onBack: () => void;
  onOpen: (id: string) => void;
  /** Search the archive (the mail sent to the address the message came by). */
  onSearch: (params: SearchParams) => void;
  /** Reply, reply all or forward this message. */
  onCompose: (mode: ComposeMode) => void;
  /** An action on the message (read flag, star, move, trash), done by the app in the background in turn with the
   * others and shown in the list at once (`change`); it tells `label` when it is done, why not when it is not (unless
   * `quiet` and only the connection is missing). Resolves true when it is done or waits in the outbox, false when it
   * is not done and was taken back. */
  onAction: (action: SingleAction, subject: string | null, change: Change, label: string | null, quiet?: boolean) => Promise<boolean>;
  onError: (error: unknown) => void;
  onNotice: (text: string) => void;
}

/** One message. A message in the offline copy is shown from it at once, then from the server when it answers (with
 * its current folders and flags; opening an unread message marks it read there); without an answer the copy stays.
 * Read, star, move and trash show at once, here and in the list (a move or trash goes back to it straight away), and
 * are done in the background, one after another (outbox.perform): the app never waits for Yahoo. Without a connection
 * they go to the outbox (outbox.ts) and show in the copy at once; opening an unread message from the copy marks it
 * read that way. An action the server refuses is taken back, and the app says why. */
export function MessageView({ id, folders, folder, me, onBack, onOpen, onSearch, onCompose, onAction, onError, onNotice }: Props) {
  const [msg, setMsg] = useState<Message | null>(null);
  /** Shown from the offline copy: the server has not answered. */
  const [fromCopy, setFromCopy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const online = useOnline();
  const [remote, setRemote] = useState(false);
  /** Restoring: the only action the view waits for (it shows the message where it is restored to). */
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);
  const [thread, setThread] = useState<ThreadRow[] | null>(null);
  const [viewer, setViewer] = useState<{ url: string; name: string } | null>(null);
  const [pdf, setPdf] = useState<{ file: Blob; name: string } | null>(null);
  const [reload, setReload] = useState(0);
  const [route, setRoute] = useState(false);
  const top = useRef<HTMLDivElement>(null);
  /** The star set here since the message was opened: the server's answer to the opening may have been asked for before
   * it, and must not show the star as it was. */
  const star = useRef<{ id: string; flagged: boolean } | null>(null);

  /** The read flag or the star shown here, unless another message is on screen by now. */
  const showFlags = (messageId: string, flags: Partial<Pick<Message, "unread" | "flagged">>) => setMsg((m) => (m?.id === messageId ? { ...m, ...flags } : m));

  useEffect(() => {
    let live = true;
    setMsg(null);
    setFromCopy(false);
    setFailed(null);
    setRemote(false);
    setMoving(false);
    setThread(null);
    setRoute(false);
    star.current = null;
    top.current?.scrollTo({ top: 0 });
    const fresh = isOnline() ? api.message(id) : null;
    fresh?.catch(() => {}); // handled below, after the copy has been looked at
    /** The message marked read on opening: not yet, asked (shown read from then on), or refused. */
    let read: "no" | "asked" | "failed" = "no";
    /** Shows the message, from the copy or the server, and marks it read on the first one that shows it unread: at once
     * on screen and in the list, done in the background (a message that could not be marked read is still shown,
     * unread again). The copy shows at once, so a message read there and left straight away is marked read too. */
    const show = (m: Message, copy: boolean) => {
      if (read === "no" && m.unread && m.folders.length > 0) {
        read = "asked";
        onAction({ kind: "read", message: m.id, read: true }, m.subject, { unread: false }, null, true).then((ok) => {
          if (ok) return;
          read = "failed";
          showFlags(m.id, { unread: true });
        });
      }
      setMsg({ ...m, ...(read === "asked" && { unread: false }), ...(star.current?.id === m.id && { flagged: star.current.flagged }) });
      setFromCopy(copy);
    };
    (async () => {
      const kept = await cachedMessage(id);
      if (!live) return;
      if (kept) show(kept, true);
      let m: Message;
      try {
        if (!fresh) throw new NetworkError();
        m = await fresh;
      } catch (e) {
        if (!live) return;
        if (kept && isNetworkError(e)) return; // reading from the copy (marked read in the outbox)
        if (isNetworkError(e)) setFailed("No connection to the server, and this message is not kept on this device.");
        else {
          setFailed("The message could not be loaded.");
          onError(e);
        }
        return;
      }
      if (!live) return;
      show(m, false);
    })();
    return () => {
      live = false;
    };
  }, [id, reload]);

  useEffect(() => () => viewer && URL.revokeObjectURL(viewer.url), [viewer]);

  const act = async (label: string, run: () => Promise<unknown>, after: () => void) => {
    setBusy(true);
    try {
      await run();
      onNotice(label);
      after();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  if (failed) {
    return (
      <div class="reader">
        <Toolbar onBack={onBack} />
        <p class="empty">{failed}</p>
      </div>
    );
  }
  if (!msg) {
    return (
      <div class="reader">
        <Toolbar onBack={onBack} />
        <div class="sentinel">
          <span class="spinner" aria-label="Loading" />
        </div>
      </div>
    );
  }

  const inYahoo = msg.folders.length > 0;
  /** The folder a move or trash takes the message from: the one it was opened from, as the server checks. */
  const from = actionFolder(msg.folders, folder) ?? "";
  const remoteContent = msg.html !== null && hasRemoteContent(msg.html);
  const attachments = msg.attachments.filter((a) => !a.inline);
  /** A move or trash: the message leaves the list at once and the app goes back to it; the action follows. */
  const leave = (label: string, action: SingleAction) => {
    onAction(action, msg.subject, { removed: true }, label);
    onBack();
  };
  const toggleRead = () => {
    const unread = !msg.unread;
    showFlags(msg.id, { unread });
    onAction({ kind: "read", message: msg.id, read: !unread }, msg.subject, { unread }, unread ? "Marked as unread" : "Marked as read").then(
      (ok) => ok || showFlags(msg.id, { unread: !unread }),
    );
  };
  const toggleStar = () => {
    const flagged = !msg.flagged;
    const set = { id: msg.id, flagged };
    star.current = set;
    showFlags(msg.id, { flagged });
    onAction({ kind: "flag", message: msg.id, flagged }, msg.subject, { flagged }, flagged ? "Starred" : "Star removed").then((ok) => {
      if (ok) return;
      if (star.current === set) star.current = null;
      showFlags(msg.id, { flagged: !flagged });
    });
  };

  const openAttachment = async (a: Attachment, fetching: Required<Fetching>) => {
    try {
      const name = a.filename || `attachment-${a.part_index}`;
      // A pass, an event or a contact card goes to Wallet, Calendar or Contacts, which the phone does only for a link
      // that serves the file as its type: a copy in Blob storage (another origin), opened from here. Without the link
      // (offline, or the server could not make one) it is downloaded like any other file.
      if (attachmentKind(a.mime || "", name) === "handoff" && isOnline()) {
        const link = await api.attachmentLink(msg.id, a.part_index, fetching.signal).catch(() => null);
        if (fetching.signal.aborted) return;
        if (link) {
          location.assign(link.url);
          return;
        }
      }
      const blob = await loadAttachment(msg.id, a.part_index, fetching);
      if (fetching.signal.aborted) return; // cancelled, or the message was left
      const mime = (a.mime || blob.type || "").toLowerCase();
      // Images open in the app (an <img> never runs scripts, SVG included), and PDFs (drawn by PDF.js, as images);
      // plain text in a new tab; all else, HTML and SVG documents especially, is downloaded and never opened as a page
      // of this origin.
      const kind = attachmentKind(mime, name);
      if (kind === "image") {
        setViewer({ url: URL.createObjectURL(blob), name });
        return;
      }
      if (kind === "pdf") {
        setPdf({ file: blob, name });
        return;
      }
      if (kind === "text") {
        const url = URL.createObjectURL(new Blob([blob], { type: mime }));
        // Not the "noopener" feature: with it window.open always returns null, which reads as a blocked popup and
        // downloads the file as well. Cut the opener by hand instead.
        const tab = window.open(url, "_blank");
        if (tab) {
          tab.opener = null;
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
          return;
        }
        URL.revokeObjectURL(url);
      }
      download(blob, name, "application/octet-stream");
    } catch (e) {
      if (!fetching.signal.aborted) onError(e);
    }
  };

  const showThread = async () => {
    if (thread) {
      setThread(null);
      return;
    }
    try {
      const rows = (await api.thread(msg.id)).messages;
      if (rows.length > 1) setThread(rows);
      else onNotice("No replies or earlier messages in the archive");
    } catch (e) {
      onError(e);
    }
  };

  return (
    <div class="reader" ref={top}>
      <Toolbar onBack={onBack}>
        <button class="icon-button" title="Reply" onClick={() => onCompose("reply")}>
          <Icon name="reply" />
        </button>
        {hasMoreRecipients(msg, me) && (
          <button class="icon-button" title="Reply all" onClick={() => onCompose("all")}>
            <Icon name="replyAll" />
          </button>
        )}
        <button class="icon-button" title="Forward" onClick={() => onCompose("forward")}>
          <Icon name="forward" />
        </button>
        {inYahoo && (
          <button class={`icon-button ${msg.flagged ? "starred" : ""}`} title={msg.flagged ? "Remove the star" : "Star"} aria-pressed={msg.flagged} onClick={toggleStar}>
            <Icon name="star" filled={msg.flagged} />
          </button>
        )}
        {inYahoo && (
          <button class="icon-button" title={msg.unread ? "Mark as read" : "Mark as unread"} onClick={toggleRead}>
            <Icon name={msg.unread ? "read" : "unread"} />
          </button>
        )}
        {inYahoo && (
          <button class="icon-button" title="Move to folder" onClick={() => setMoving(!moving)}>
            <Icon name="move" />
          </button>
        )}
        {inYahoo && (
          <button class="icon-button" title="Move to Trash" onClick={() => leave("Moved to Trash", { kind: "trash", message: msg.id, from })}>
            <Icon name="trash" />
          </button>
        )}
        {!inYahoo && (
          <button
            class="text-button"
            disabled={busy}
            onClick={() => act("Restored to Inbox", () => api.restore(msg.id, "Inbox"), () => setReload(reload + 1))}
          >
            <Icon name="restore" /> Restore
          </button>
        )}
      </Toolbar>

      {moving && (
        <div class="move-picker">
          <span>Move to</span>
          <div class="move-options">
            {folders
              .filter((f) => !msg.folders.some((l) => l.folder === f))
              .map((f) => (
                <button key={f} class="chip" onClick={() => leave(`Moved to ${f}`, { kind: "move", message: msg.id, from, folder: f })}>
                  {f}
                </button>
              ))}
          </div>
        </div>
      )}

      <header class="reader-head">
        <h1>{msg.subject || "(no subject)"}</h1>
        <div class="reader-from">
          <strong>{sender(msg.from_name, msg.from_addr)}</strong>
          {msg.from_name && msg.from_addr && <span class="muted"> &lt;{msg.from_addr}&gt;</span>}
        </div>
        {msg.to && msg.to.length > 0 && <div class="muted small">To: {msg.to.join(", ")}</div>}
        {msg.cc && msg.cc.length > 0 && <div class="muted small">Cc: {msg.cc.join(", ")}</div>}
        {showDelivery(msg.delivery) && (
          <>
            <button class="delivery" aria-expanded={route} title="How this message reached you" onClick={() => setRoute(!route)}>
              <Icon name="via" size={14} />
              <span>
                Sent to <strong>{msg.delivery.sent_to}</strong>
              </span>
              <span class="delivery-end">
                {!msg.delivery.addressed && <span class="tag">not in To/Cc</span>}
                <span class="delivery-toggle">
                  <Icon name="chevron" size={14} />
                </span>
              </span>
            </button>
            {route && (
              <DeliveryPath delivery={msg.delivery} onSearch={(sentTo) => onSearch({ sent_to: sentTo, include_deleted: true, sort: "newest" })} />
            )}
          </>
        )}
        <div class="muted small">{fullDate(msg.date)}</div>
        <div class="reader-tags">
          {msg.folders.map((l) => (
            <span class={`tag ${l.folder === folder ? "current" : ""}`} key={l.folder}>
              {l.folder}
            </span>
          ))}
          {msg.deleted_at && <span class="tag warn">Deleted from Yahoo {listDate(msg.deleted_at)}</span>}
          <button class="chip" onClick={showThread}>
            <Icon name="thread" size={16} /> {thread ? "Hide conversation" : "Conversation"}
          </button>
        </div>
      </header>

      {thread && (
        <ul class="thread" role="list">
          {thread.map((t) => (
            <li key={t.id}>
              <button class={`row ${t.id === msg.id ? "selected" : ""}`} onClick={() => t.id !== msg.id && onOpen(t.id)}>
                <span class="row-top">
                  <span class="row-from">{sender(t.from_name, t.from_addr)}</span>
                  <span class="row-date">{listDate(t.date)}</span>
                </span>
                <span class="row-snippet">{t.snippet}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {attachments.length > 0 && (
        <ul class="attachments" role="list">
          {attachments.map((a) => (
            <li key={`${msg.id}#${a.part_index}`}>
              <AttachmentButton attachment={a} onOpen={(fetching) => openAttachment(a, fetching)} />
            </li>
          ))}
        </ul>
      )}

      {fromCopy && !online && (
        <div class="banner offline-banner" role="status">
          <span>Offline: this is the copy kept on this device.</span>
        </div>
      )}

      {remoteContent && !remote && (
        <div class="banner">
          <span>Remote images are blocked.</span>
          <button class="text-button" onClick={() => setRemote(true)}>
            Show images
          </button>
        </div>
      )}

      {msg.html !== null ? <MessageFrame html={msg.html} allowRemote={remote} /> : <pre class="plain">{msg.body_text || ""}</pre>}

      {viewer && (
        <div class="viewer" onClick={() => setViewer(null)} role="dialog" aria-label={viewer.name}>
          <img src={viewer.url} alt={viewer.name} />
          <button class="icon-button viewer-close" title="Close">
            <Icon name="close" />
          </button>
        </div>
      )}

      {pdf && (
        <PdfViewer file={pdf.file} name={pdf.name} onClose={() => setPdf(null)} onDownload={() => download(pdf.file, pdf.name, "application/pdf")} />
      )}
    </div>
  );
}

/** The addresses the message was delivered to on its way, oldest first: the one the sender used, each forward, the
 * mailbox; each with the headers that name it. Then a search for all mail sent to the first one. */
function DeliveryPath({ delivery, onSearch }: { delivery: Delivery; onSearch: (sentTo: string) => void }) {
  return (
    <>
      <ol class="delivery-path" aria-label={delivery.summary}>
        {delivery.path.map((step) => (
          <li key={step.address}>
            <span class="delivery-address">{step.address}</span>
            <span class="muted">{[step.note, step.sources.join(", ")].filter(Boolean).join(" · ")}</span>
          </li>
        ))}
      </ol>
      <button type="button" class="text-button delivery-search" onClick={() => onSearch(delivery.sent_to)}>
        All mail sent to {delivery.sent_to}
      </button>
    </>
  );
}

/** An attachment, opened on a tap. A file not kept on the device comes from the server, which reads it out of the
 * archived message first: a spinner from the tap, then how much of it has arrived. A tap meanwhile cancels it (the ×),
 * and so does leaving the message. */
function AttachmentButton({ attachment: a, onOpen }: { attachment: Attachment; onOpen: (fetching: Required<Fetching>) => Promise<void> }) {
  /** The bytes received while opening (0: none yet, or from the copy); null when not opening. */
  const [received, setReceived] = useState<number | null>(null);
  const opening = useRef<AbortController | null>(null);
  useEffect(() => () => opening.current?.abort(), []);
  const name = a.filename || `Attachment ${a.part_index}`;

  const tap = async () => {
    if (opening.current) {
      opening.current.abort();
      opening.current = null;
      setReceived(null);
      return;
    }
    const ctl = new AbortController();
    opening.current = ctl;
    setReceived(0);
    try {
      await onOpen({ progress: (n) => opening.current === ctl && setReceived(n), signal: ctl.signal });
    } finally {
      if (opening.current === ctl) {
        opening.current = null;
        setReceived(null);
      }
    }
  };

  const busy = received !== null;
  const percent = received && a.size ? Math.min(100, Math.floor((received / a.size) * 100)) : null;
  return (
    <button class="attachment" onClick={tap} aria-busy={busy} title={busy ? `Cancel download of ${name}` : undefined}>
      {busy ? <span class="spinner small-spinner" /> : <Icon name={(a.mime || "").startsWith("image/") ? "image" : "clip"} size={16} />}
      <span class="attachment-name">{name}</span>
      <span class="muted small">{percent !== null ? `${percent}%` : size(received || a.size)}</span>
      {busy && <Icon name="close" size={16} />}
      {percent !== null && <span class="attachment-progress" style={{ width: `${percent}%` }} />}
    </button>
  );
}

/** Saves the file (on an iPhone: the browser sheet that offers it to Preview, Files and the share sheet). */
function download(blob: Blob, name: string, type: string) {
  const url = URL.createObjectURL(new Blob([blob], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function Toolbar({ onBack, children }: { onBack: () => void; children?: preact.ComponentChildren }) {
  return (
    <div class="toolbar reader-toolbar">
      <button class="icon-button back" title="Back" onClick={onBack}>
        <Icon name="back" />
      </button>
      <span class="spacer" />
      {children}
    </div>
  );
}

/** The sanitized HTML in a sandboxed frame, sized to its content. Fixed widths wider than the screen are given up
 * first; what is still wider is scaled down. */
function MessageFrame({ html, allowRemote }: { html: string; allowRemote: boolean }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(200);

  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    let observer: ResizeObserver | null = null;
    const measure = () => {
      const doc = el.contentDocument;
      if (doc?.documentElement) setHeight(Math.ceil(doc.documentElement.scrollHeight) + 4);
    };
    // Widths and zoom are set only on load, when an image arrives and when the window resizes; the observer only
    // re-measures the height (setting zoom from it would resize the body again and loop).
    const fit = () => {
      const doc = el.contentDocument;
      if (!doc?.body) return;
      doc.body.style.zoom = "";
      const room = el.clientWidth;
      if (doc.documentElement.scrollWidth > room + 2) fitFixedWidths(doc);
      const wide = doc.documentElement.scrollWidth;
      if (wide > room + 2) doc.body.style.zoom = String(room / wide);
      measure();
    };
    const onLoad = () => {
      fit();
      const doc = el.contentDocument;
      if (!doc?.body) return;
      doc.querySelectorAll("img").forEach((img) => img.complete || img.addEventListener("load", fit, { once: true }));
      observer?.disconnect();
      observer = new ResizeObserver(measure);
      observer.observe(doc.body);
    };
    el.addEventListener("load", onLoad);
    // A srcdoc can finish loading before this effect runs; then there is no load event left to wait for.
    if (el.contentDocument?.readyState === "complete") onLoad();
    const onResize = () => fit();
    addEventListener("resize", onResize);
    return () => {
      el.removeEventListener("load", onLoad);
      removeEventListener("resize", onResize);
      observer?.disconnect();
    };
  }, [html, allowRemote]);

  return (
    <iframe
      ref={frame}
      class="message-frame"
      title="Message"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      referrerpolicy="no-referrer"
      srcdoc={frameDocument(html, allowRemote)}
      style={{ height }}
    />
  );
}
